import { inject, Injectable, Signal } from '@angular/core';
import { Auth, user } from '@angular/fire/auth';
import {
  getAuth,
  createUserWithEmailAndPassword,
  UserCredential,
  signInWithEmailAndPassword,
  User,
  sendPasswordResetEmail,
  signOut,
  onAuthStateChanged,
  signInWithPopup,
  GoogleAuthProvider,
  updatePassword
} from '@firebase/auth';
import { Observable, firstValueFrom } from 'rxjs';
import { Router } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { UserProfileService } from '../services/user-profile.service';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  private auth: Auth = inject(Auth);
  private router: Router = inject(Router);
  public user: Signal<User | null | undefined> = toSignal(user(this.auth));
  private baseUrl = environment.baseUrl;

  constructor(
    private userProfileService: UserProfileService,
    private http: HttpClient
  ) {
    try {
      this.auth = getAuth();
    } catch (e) {
      console.error(e);
    }
  }



  login(email: string, password: string): Promise<UserCredential> {
    return signInWithEmailAndPassword(this.auth, email, password);
  }

  register(email: string, password: string): Promise<UserCredential> {
    return createUserWithEmailAndPassword(this.auth, email, password);
  }

  async loginWithGoogle() {
    const response = await signInWithPopup(this.auth, new GoogleAuthProvider());
    if (response.user) {
      this.router.navigate(['/']);
    }
  }

  async logout() {
    await signOut(this.auth);
    this.userProfileService.clearUserProfile();
    this.router.navigate(['/auth/login']);
  }

  getCurrentUser(): Observable<User | null> {
    return new Observable(observer => {
      onAuthStateChanged(this.auth, user => {
        observer.next(user);
        observer.complete();
      });
    });
  }



  resetPassword(email: string): Promise<void> {
    return sendPasswordResetEmail(this.auth, email);
  }

  updatePassword(newPassword: string): Promise<void> {
    const currentUser = this.auth.currentUser;
    if (!currentUser) {
      return Promise.reject(new Error('No user logged in'));
    }
    return updatePassword(currentUser, newPassword);
  }


  async getUserByEmail(email: string): Promise<{
    success: boolean;
    message: string;
    user?: { id: string; email: string };
  }> {
    try {
      const response = await firstValueFrom(
        this.http.get<{
          id: string;
          email: string;
        }>(
          `${this.baseUrl}/users/email/${email}`,
        )
      );

      return {
        success: true,
        message: 'User found',
        user: response
      };

    } catch (error) {
      if (error instanceof HttpErrorResponse) {

        if (error.status === 404) {
          return {
            success: false,
            message: 'Email not found'
          };
        }

        return {
          success: false,
          message: error.error?.message || 'Failed to get user'
        };
      }

      return {
        success: false,
        message: 'Unknown error'
      };
    }
  }



  async sendResetLinkIfEmailExists(email: string): Promise<{
    success: boolean;
    message: string;
  }> {
    try {
      const result = await this.getUserByEmail(email);

      if (!result.success || !result.user) {
        return {
          success: false,
          message: 'This email is not registered'
        };
      }

      await sendPasswordResetEmail(this.auth, email);

      return {
        success: true,
        message: 'Password reset link sent to your email'
      };

    } catch (error) {
      console.error(error);
      return {
        success: false,
        message: 'Failed to send reset password email'
      };
    }
  }
}
